import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocateFixed } from 'lucide-react';
import { Button } from '../ui/button';
import ModelControlsHelp from '../crag/ModelControlsHelp';
import { isDrawableModelLine } from '../crag/useModelLine';
import type { CragRouteModelRoute } from '../crag/CragRouteModel';
import type { Climb } from '../../types/climb';
import type { ModelView } from '../../types/crag-route';
import { CragModelCanvas } from './CragModelCanvas';
import { cragModelFraming, OVERLAY_BUTTON } from './crag-model-framing';

const IGNORE_SELECTION = () => {};

interface CragRouteModelCoverProps {
  climb: Climb;
  url: string;
  byteSize: number | null;
  /** The sector's view: limits on where the camera can be turned to. */
  sectorView: ModelView | null;
  onFailed: () => void;
}

/**
 * One route on the crag's 3D model, on the route's own page: its line and nobody
 * else's, the camera it was saved from. It fills the cover, whose controls stay
 * above it. `data-swipe-ignore` because a sideways drag here turns the model, and
 * must not also step to the next climb of the list.
 */
export function CragRouteModelCover({ climb, url, byteSize, sectorView, onFailed }: CragRouteModelCoverProps) {
  const { t } = useTranslation();
  const [loadRequested, setLoadRequested] = useState(false);
  const [request, setRequest] = useState(0);

  const framing = useMemo(
    () =>
      cragModelFraming({
        sectorKey: climb.id,
        sectorView,
        climbs: [climb],
        selectedClimbId: climb.id,
        request,
        recentred: true,
      }),
    [climb, sectorView, request],
  );

  const routes = useMemo(
    (): CragRouteModelRoute[] =>
      isDrawableModelLine(climb.model_line)
        ? [{ id: climb.id, name: climb.name ?? '', model_line: climb.model_line }]
        : [],
    [climb.id, climb.name, climb.model_line],
  );

  return (
    <div data-testid="climb-model-cover" data-swipe-ignore className="absolute inset-0 bg-zinc-900">
      <CragModelCanvas
        url={url}
        byteSize={byteSize}
        loadRequested={loadRequested}
        onLoadRequested={() => setLoadRequested(true)}
        onFailed={onFailed}
        routes={routes}
        framing={framing}
        selectedRouteId={climb.id}
        viewLimits={sectorView}
        onSelectRoute={IGNORE_SELECTION}
      />

      {/* Under the view switch, which belongs to the cover. */}
      <div className="absolute right-2 top-11 flex flex-col items-end gap-1.5">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('cragProfile.model3d.recentre')}
          title={t('cragProfile.model3d.recentre')}
          onClick={() => setRequest((count) => count + 1)}
          className={`h-8 w-8 ${OVERLAY_BUTTON}`}
        >
          <LocateFixed className="h-4 w-4" />
        </Button>
        <ModelControlsHelp
          canPan={!sectorView?.limits || sectorView.limits.pan > 0}
          canPivot={!sectorView?.limits}
          className={`h-8 w-8 ${OVERLAY_BUTTON}`}
        />
      </div>
    </div>
  );
}
